import { useState, useEffect, useMemo } from 'react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts'
import toast, { Toaster } from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { useSensorData } from '../context/SensorDataContext'
import { iotControllerApi } from '../api/services'

const DYNAMIC_COLORS = [
  '#22c55e', '#3b82f6', '#f97316', '#8b5cf6', '#06b6d4',
  '#ec4899', '#84cc16', '#f59e0b', '#ef4444', '#14b8a6', '#a855f7', '#0ea5e9'
]

const buildChannels = paramMappings => {
  const entries = Object.entries(paramMappings || {})
  if (entries.length === 0) return []
  return entries.map(([key, { display_name, unit }], i) => ({
    key,
    color: DYNAMIC_COLORS[i % DYNAMIC_COLORS.length],
    label: display_name || key.toUpperCase(),
    unit: unit || ''
  }))
}

// ── Custom dark tooltip ───────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  const rows = payload.filter(p => p.value !== null && p.value !== undefined)
  return (
    <div className='bg-gray-900 text-white rounded-lg px-3 py-2.5 shadow-2xl min-w-[170px]'>
      <p className='text-xs font-semibold mb-1.5 text-gray-300'>{label}</p>
      {rows.map(p => (
        <div key={p.dataKey} className='flex items-center justify-between gap-4'>
          <div className='flex items-center gap-1.5'>
            <span className='w-2 h-2 rounded-full' style={{ backgroundColor: p.color }} />
            <span className='text-[11px] text-gray-300'>{p.name}</span>
          </div>
          <span className='text-[11px] font-mono font-bold text-white'>{p.value}</span>
        </div>
      ))}
    </div>
  )
}

const STATUS_CFG = {
  online:  { badge: 'text-green-700 bg-green-50 border border-green-200',  dot: 'bg-green-500 animate-pulse', label: 'Online'  },
  offline: { badge: 'text-gray-500 bg-gray-50 border border-gray-200',     dot: 'bg-gray-300',                label: 'Offline' }
}

// ── Single chart card ─────────────────────────────────────────────────────────
const ControllerChart = ({ controller, liveStatus, onClose }) => {
  const { dynamicChartData, paramMappingsCache } = useSensorData()

  const mappings = paramMappingsCache[controller.id] || {}
  const allChannels = useMemo(
    () => buildChannels(Object.keys(mappings).length > 0 ? mappings : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controller.id, JSON.stringify(mappings)]
  )

  const [selectedParam, setSelectedParam] = useState('all')

  const configuredChannels = useMemo(() => {
    if (selectedParam === 'all') return allChannels
    return allChannels.filter(ch => ch.key === selectedParam)
  }, [allChannels, selectedParam])

  const displayData = dynamicChartData[controller.id] || []
  const hasData = displayData.length > 0
  const latestPoint = displayData.slice(-1)[0] || null

  const cfg = STATUS_CFG[liveStatus] || STATUS_CFG.offline

  const yDomain = useMemo(() => {
    if (selectedParam === 'all' || !mappings[selectedParam]) return ['auto', 'auto']
    const m = mappings[selectedParam]
    return [m.min_value ?? 'auto', m.max_value ?? 'auto']
  }, [selectedParam, mappings])

  return (
    <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex flex-col gap-4'>
      {/* Header */}
      <div className='flex items-center justify-between flex-wrap gap-2'>
        <div className='flex items-center gap-2.5 flex-wrap'>
          <span className='text-sm font-bold text-gray-800'>{controller.name}</span>
          <span className={`flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-0.5 rounded-full ${cfg.badge}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
            {cfg.label}
          </span>
          {controller.asset_names?.[0] && (
            <span className='text-[10px] bg-purple-50 text-purple-700 border border-purple-200 rounded px-1.5 py-0.5'>
              {controller.asset_names[0]}
            </span>
          )}
          {controller.location_name && (
            <span className='text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5'>
              {controller.location_name}
            </span>
          )}
        </div>
        <div className='flex items-center gap-2'>
          {allChannels.length > 1 && (
            <select
              value={selectedParam}
              onChange={e => setSelectedParam(e.target.value)}
              className='text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-400'
            >
              <option value='all'>All Parameters</option>
              {allChannels.map(ch => (
                <option key={ch.key} value={ch.key}>
                  {ch.label}{ch.unit ? ` (${ch.unit})` : ''}
                </option>
              ))}
            </select>
          )}
          <button onClick={onClose} className='text-gray-400 hover:text-gray-600' title='Hide chart'>
            <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
            </svg>
          </button>
        </div>
      </div>

      {/* Chart */}
      {!hasData ? (
        <div className='flex flex-col items-center justify-center h-52 text-gray-400'>
          <div className={`w-8 h-8 border-2 border-gray-100 rounded-full animate-spin mb-3 ${
            liveStatus !== 'offline' ? 'border-t-green-400' : 'border-t-gray-300'
          }`} />
          <p className='text-xs font-medium'>Waiting for data…</p>
          <p className='text-[10px] mt-1 text-gray-300 font-mono'>{controller.device_id}</p>
        </div>
      ) : configuredChannels.length === 0 ? (
        <div className='flex flex-col items-center justify-center h-52 text-gray-400'>
          <p className='text-xs font-medium'>No param mappings configured</p>
          <p className='text-[10px] mt-1'>Set params via the IoT Controllers page</p>
        </div>
      ) : (
        <ResponsiveContainer width='100%' height={220}>
          <LineChart data={displayData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray='3 3' stroke='#f3f4f6' vertical={false} />
            <XAxis dataKey='time' tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} interval='preserveStartEnd' minTickGap={40} />
            <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} tickLine={false} axisLine={false} domain={yDomain} />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#e5e7eb', strokeWidth: 1 }} />
            {configuredChannels.length > 1 && (
              <Legend wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }} />
            )}
            {configuredChannels.map(({ key, color, label, unit }) => (
              <Line
                key={key}
                type='monotone'
                dataKey={key}
                name={`${label}${unit ? ` (${unit})` : ''}`}
                stroke={color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4, strokeWidth: 0, fill: color }}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}

      {/* Latest values strip */}
      {latestPoint && configuredChannels.length > 0 && (
        <div className='flex items-center gap-4 pt-2 border-t border-gray-50 flex-wrap'>
          {configuredChannels.map(({ key, color, label, unit }) => {
            const raw = latestPoint[key]
            const val = raw !== null && raw !== undefined ? parseFloat(Number(raw).toFixed(2)) : null
            if (val === null) return null
            const m = mappings[key]
            const minVal = m?.min_value ?? null
            const maxVal = m?.max_value ?? null
            const isBelow = minVal !== null && val < minVal
            const isAbove = maxVal !== null && val > maxVal
            const outOfRange = isBelow || isAbove
            return (
              <div key={key} className={`text-center min-w-[52px] rounded-lg px-1.5 py-0.5 ${outOfRange ? 'bg-red-50' : ''}`}>
                <p className='text-[10px] text-gray-400'>{label}</p>
                <p
                  className='text-sm font-bold font-mono'
                  style={{ color: outOfRange ? '#ef4444' : color }}
                  title={outOfRange ? `Out of range: ${isBelow ? `below min (${minVal})` : `above max (${maxVal})`}` : undefined}
                >
                  {val}
                  {unit && <span className='text-[10px] font-normal ml-0.5 text-gray-400'>{unit}</span>}
                </p>
                {outOfRange && (
                  <p className='text-[9px] text-red-400 font-semibold'>
                    {isBelow ? `↓ min ${minVal}` : `↑ max ${maxVal}`}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
const AssetIoTDataMonitoring = () => {
  const { user, activeTenant } = useAuth()
  const resolvedTenant = activeTenant?.id || user?.tenant_id
  const { loadHistoricalData, loadParamMappings, livePayloads } = useSensorData()

  const [controllers, setControllers] = useState([])
  const [isLoading,   setIsLoading]   = useState(true)
  const [visibleIds,  setVisibleIds]  = useState([])
  const [activeTab,   setActiveTab]   = useState('online')

  useEffect(() => {
    if (!resolvedTenant) return
    const load = async () => {
      try {
        setIsLoading(true)
        const res = await iotControllerApi.getAll(resolvedTenant)
        const list = res.data?.data || res.data || []
        setControllers(list)
        setVisibleIds(list.map(c => c.id))
        loadHistoricalData(list)
        loadParamMappings(list)
      } catch {
        toast.error('Failed to load controllers')
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [resolvedTenant])

  const getStatus = ctrl => {
    const s = (livePayloads[ctrl.id]?.status || ctrl.status || 'offline').toLowerCase()
    return s === 'offline' ? 'offline' : 'online'
  }

  const counts = useMemo(
    () => ({
      all:    controllers.length,
      online: controllers.filter(c => getStatus(c) === 'online').length
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [controllers, livePayloads]
  )

  const tabControllers = useMemo(() => {
    if (activeTab === 'all') return controllers
    return controllers.filter(c => getStatus(c) === activeTab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controllers, activeTab, livePayloads])

  const visibleControllers = tabControllers.filter(c => visibleIds.includes(c.id))
  const hiddenControllers  = tabControllers.filter(c => !visibleIds.includes(c.id))

  const TABS = [
    { id: 'all',    label: `All (${counts.all})`      },
    { id: 'online', label: `Online (${counts.online})` }
  ]

  if (isLoading)
    return (
      <div className='min-h-screen flex items-center justify-center bg-gray-50 text-gray-500'>
        Loading…
      </div>
    )

  return (
    <div className='bg-gray-50 min-h-screen p-8 font-sans'>
      <Toaster position='top-right' />

      {/* Header */}
      <div className='mb-6'>
        <h1 className='text-2xl font-bold text-gray-800'>Asset-IoT Data Monitoring</h1>
        <p className='text-sm text-gray-500 mt-1'>Real-time sensor data per controller</p>
      </div>

      {/* Status tabs */}
      <div className='bg-white rounded-xl border border-gray-200 shadow-sm p-3 mb-6 flex items-center gap-1'>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? tab.id === 'online' ? 'bg-green-600 text-white' : 'bg-gray-800 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Restore hidden charts */}
      {hiddenControllers.length > 0 && (
        <div className='mb-5 flex items-center gap-2 flex-wrap'>
          <span className='text-xs text-gray-500 font-medium'>Hidden:</span>
          {hiddenControllers.map(c => (
            <button
              key={c.id}
              onClick={() => setVisibleIds(prev => [...prev, c.id])}
              className='flex items-center gap-1 px-3 py-1 text-xs bg-white border border-gray-200 rounded-full text-gray-600 hover:border-blue-400 hover:text-blue-700 transition-colors shadow-sm'
            >
              <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
              </svg>
              {c.name}
            </button>
          ))}
        </div>
      )}

      {/* Empty states */}
      {controllers.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-20 text-gray-400'>
          <svg className='w-12 h-12 mb-3 opacity-30' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={1.5}
              d='M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.143 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0' />
          </svg>
          <p className='text-base font-medium'>No IoT controllers found</p>
          <p className='text-sm mt-1'>Add controllers in IoT Controllers first</p>
        </div>
      ) : tabControllers.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-20 text-gray-400'>
          <div className='w-10 h-10 mb-3 rounded-full flex items-center justify-center bg-green-50'>
            <span className='w-3 h-3 rounded-full bg-green-300' />
          </div>
          <p className='text-base font-medium'>No online controllers right now</p>
          <p className='text-sm mt-1'>Controllers sending data will appear here</p>
        </div>
      ) : visibleControllers.length === 0 ? (
        <div className='flex flex-col items-center justify-center py-20 text-gray-400'>
          <p className='text-base font-medium'>All charts hidden</p>
          <p className='text-sm mt-1'>Use the buttons above to restore charts</p>
        </div>
      ) : (
        <div className='grid grid-cols-1 xl:grid-cols-2 gap-6'>
          {visibleControllers.map(ctrl => (
            <ControllerChart
              key={ctrl.id}
              controller={ctrl}
              liveStatus={getStatus(ctrl)}
              onClose={() => setVisibleIds(prev => prev.filter(v => v !== ctrl.id))}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default AssetIoTDataMonitoring
